import type { MiddlewareAPI } from 'redux';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/fromPromise';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/mergeMap';
import type { ActionsObservable } from 'redux-observable';
import { Observable } from 'rxjs/Observable';
import { SERVICE_DESK_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { OTHER_DEPARTMENT } from '@atlassian/jira-servicedesk-project-department-type-select/src/ui/constants';
import projectService from '../../services/project';
import type {
	ProjectDetails,
	ProjectDetailsUpdateSimplified,
	ProjectDetailsUpdate,
	ProjectTeamType,
} from '../../services/project/types';
import type { Action } from '../../state/actions';
import {
	projectContextSelector,
	getOnDetailsUpdated,
	getIsAdmin,
	getProjectTypeSelector,
} from '../../state/context/selectors';
import {
	SAVE_PROJECT_DETAILS,
	saveProjectDetailsSuccess,
	saveProjectDetailsFail,
} from '../../state/entities/actions';
import type { AppState } from '../../state/types';
import {
	projectAvatarIdUiSelector,
	projectKeyToSaveSelector,
	projectNameToSaveSelector,
	projectLeadAccountIdUiSelector,
	assigneeTypeUiSelector,
	projectCategoryIdUiSelector,
	projectDescriptionToSaveSector,
	projectUrlToSaveSelector,
	simplifiedToSaveSelector,
	projectTeamTypeToSaveSector,
	projectTeamTypeFreeTextToSaveSector,
	releasedProjectKeysSelector,
} from '../../state/ui/selectors';

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default (action$: ActionsObservable<Action>, store: MiddlewareAPI<AppState>) =>
	action$.ofType(SAVE_PROJECT_DETAILS).mergeMap(() => {
		const state = store.getState();
		const onDetailsUpdated = getOnDetailsUpdated(state);
		const simplified = simplifiedToSaveSelector(state);
		const isAdmin = getIsAdmin(state);
		const projectKey = projectKeyToSaveSelector(state);
		const projectName = projectNameToSaveSelector(state);
		const projectDescription = projectDescriptionToSaveSector(state);
		const projectUrl = projectUrlToSaveSelector(state);
		const projectTeamType = projectTeamTypeToSaveSector(state);
		const projectTeamTypeFreeText = projectTeamTypeFreeTextToSaveSector(state);
		const projectType = getProjectTypeSelector(state);

		const leadAccountId = projectLeadAccountIdUiSelector(state);
		const assigneeType = assigneeTypeUiSelector(state);
		const projectCategoryId = projectCategoryIdUiSelector(state);
		const releasedProjectKeys = releasedProjectKeysSelector(state);
		// @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
		const categoryId = parseInt(projectCategoryId, 10);

		const projectDetailsUpdateSimplified: ProjectDetailsUpdateSimplified = {
			key: projectKey,
			name: projectName,
			avatarId: projectAvatarIdUiSelector(state),
		};

		projectDetailsUpdateSimplified.leadAccountId = leadAccountId;
		projectDetailsUpdateSimplified.assigneeType = assigneeType;

		if (categoryId) {
			projectDetailsUpdateSimplified.categoryId = categoryId;
		}

		const projectDetailsUpdate: ProjectDetailsUpdate = {
			name: projectName,
			description: projectDescription,
			avatarId: projectAvatarIdUiSelector(state),
			url: projectUrl,
			leadAccountId,
			assigneeType,
		};

		const projectTeamTypeObj: ProjectTeamType = {
			teamType: projectTeamType || '',
			teamTypeUnsafeUgc:
				projectTeamType === OTHER_DEPARTMENT ? projectTeamTypeFreeText || '' : undefined,
		};

		if (isAdmin) {
			projectDetailsUpdate.key = projectKey;
			if (fg('hix-4189-releasing-historical-project-keys')) {
				projectDetailsUpdate.releasedProjectKeys = releasedProjectKeys;
				projectDetailsUpdateSimplified.releasedProjectKeys = releasedProjectKeys;
			}

			if (categoryId) {
				projectDetailsUpdate.categoryId = categoryId;
			}
		}

		/*
             updateProjectDetails - CMP implementation that carries extra props (description and url field to the db).
             updateProjectDetailsSimplified - TMP implementation.
         */
		const updateProjectDetailsObs: Observable<ProjectDetails> = Observable.fromPromise(
			!simplified
				? projectService.updateProjectDetails(projectContextSelector(state), projectDetailsUpdate)
				: projectService.updateProjectDetailsSimplified(
						projectContextSelector(state),
						projectDetailsUpdateSimplified,
					),
		);

		return updateProjectDetailsObs
			.flatMap((result) => {
				if (projectType === SERVICE_DESK_PROJECT && projectTeamType) {
					return Observable.fromPromise(
						projectService.updateProjectTeamTypeDetails(
							projectContextSelector(state),
							projectTeamTypeObj,
						),
					).map((teamTypeResult) => ({
						...result,
						projectTeamType: teamTypeResult?.teamType,
						projectTeamTypeFreeText: teamTypeResult?.teamTypeUnsafeUgc,
					}));
				}
				return Observable.of(result);
			})
			.do(({ key, name, description, avatar: { id }, url }: ProjectDetails) => {
				onDetailsUpdated({
					key,
					name,
					description,
					avatarId: id,
					url,
					projectTeamType,
					projectTeamTypeFreeText,
				});
			})
			.map(saveProjectDetailsSuccess)
			.catch((error) => Observable.of(saveProjectDetailsFail(error)));
	});
